/* eslint-disable jsx-a11y/no-autofocus */
import React, { useState, useRef, useEffect, useMemo } from 'react'
import { Badge } from '@cmsgov/ds-medicare-gov'
import { ReactComponent as CaretIcon } from '../../images/Icon_Caret.svg'

import { Drawer } from '../Drawer/Drawer'
import { useTranslation } from 'react-i18next'

import { Link, ResourceList, MobileResourceNavItem } from '../../models/Navigation'
import hamburgerIcon from '../../images/Icon_Hamburger.svg'
import classNames from 'classnames'

import events, { Event } from 'pubsub/events'

import { CATEGORIES } from '../../constants/analytics.json'
import callEventHook from '../../utils/callEventHook'
import { Button as CHButton } from '../../components/Button/Button'
import { getUnreadMessageCount, setSelectedMessageId } from '../../redux/reducers/messages'
import { getUserLoggedInState } from '../../redux/reducers/users'
import { useDispatch, useSelector } from 'react-redux'
import i18n from 'i18n/config'
import { getChangeLanguageLinkLang } from 'utils/getChangeLanguageLinkLang'
import { getOpenModal, setOpenModal } from 'redux/reducers/header'
import { createAndEmitLogoutEvent } from 'utils/logoutEvents/create-and-emit-logout-event'

interface NavLinkListProps {
  analyticsCategory: string;
  label: string;
  description?: string;
  links: Link[];
  menuName: string;
}

const NavLinkList: React.FC<NavLinkListProps> = ({ label, description, links, menuName, analyticsCategory }) => {
  const isLoggedIn = useSelector(getUserLoggedInState)

  const sendClickEvent = (link: Link) => {
    const options: Event = {
      authenticated: isLoggedIn,
      text: link.label,
      targetLocation: link.href,
      heading: menuName,
      subheading: label,
      category: analyticsCategory,
      activity: 'Link Click',
      url: window.location.href,
      language: i18n.language,
      title: document.title,
      sendToTealium: true,
      sendToCoreAPI: true,
      isMessageCenter: false

    }

    events.publish(options)
  }

  return (
    <div className='ds-u-sans ds-u-margin--3'>
      <h2 className='ds-u-font-size--md ch-c-mobile-menu__section-label ds-u-margin-bottom--2'>{label}</h2>

      {description && <p className='ds-u-font-size--base ds-u-color--base ds-u-margin-left--3 ds-u-margin-bottom--2'>{description}</p>}

      <ul className='ds-u-padding--0 ds-u-padding-left--3 ds-u-margin-top--2' style={{ listStyle: 'none' }}>
        {links.map((link: Link) => (
          <li key={link.id} className='ds-u-margin-bottom--2'>
            <CHButton
              isMbpRedirect={link.isMbpRedirect}
              className='ch-c-link ds-c-button--transparent ds-u-padding--0 ds-u-text-align--left ds-u-color--primary ds-u-font-size--base ds-u-text-decoration--none'
              href={link.href}
              onClick={() => sendClickEvent(link)}
            >
              {link.label}
            </CHButton>

          </li>
        ))}
      </ul>
    </div>
  )
}

interface SubNavProps {
  analyticsCategory: string;
  menu?: React.ReactNode;
  menuName: string;
  sections?: ResourceList[];
  onBack: () => void;
}

const SubNav: React.FC<SubNavProps> = ({ analyticsCategory, menuName, sections, onBack, menu }) => {
  const { t } = useTranslation('common')

  return (
    <>
      <button autoFocus className='ch-c-link ds-u-color--primary ch-c-mobile-nav__option-btn' onClick={onBack} type='button'>
        <span className='ds-u-display--flex ds-u-align-items--center '>
          <CaretIcon className='ch-c-caret--left ch-u-height--20 ds-u-margin-right--1' />
          <span className='ch-u-font--rubik'>{t('back')}</span>
        </span>
      </button>

      <hr className='ds-u-margin-y--0 ds-u-margin-x--3 ds-u-border--0 ds-u-fill--gray-lighter' style={{ height: '1px' }} />

      {
        sections
          ? sections.map((section: ResourceList) => (
            <NavLinkList analyticsCategory={analyticsCategory} key={section.id} menuName={menuName} {...section} />
          ))
          : menu
      }
    </>
  )
}

interface MobileNavSubListProps {
  navList: MobileResourceNavItem[];
  onNavOptionClick: ((input: any) => (void));
  bottomBorder: boolean;
  handleCloseButtonClick: () => void;
  currentOpenModal: string;
  enableMessageCenter: boolean;
}

const MobileNavSubList: React.FC<MobileNavSubListProps> = ({ handleCloseButtonClick, navList, onNavOptionClick, bottomBorder, currentOpenModal, enableMessageCenter }) => {
  const ulClasses = `ch-c-mobile-nav__options-list${bottomBorder ? ' ch-c-mobile-nav__options-list-separator' : ''}`
  const dispatch = useDispatch()
  const unreadMessageCount = useSelector(getUnreadMessageCount)
  const isLoggedIn = useSelector(getUserLoggedInState)
  const subNavDict = useMemo(() => { return {} }, []) as any

  useEffect(() => {
    onNavOptionClick(subNavDict[currentOpenModal])
  }, [currentOpenModal, onNavOptionClick, subNavDict, handleCloseButtonClick])

  return (
    <ul className={ulClasses}>
      {
        navList.map(navOption => {
          const handleBackClick = () => {
            onNavOptionClick(undefined)

            dispatch(setOpenModal('mobileMenu'))

            const options: Event = {
              authenticated: isLoggedIn,
              text: 'Back',
              targetLocation: 'Back to main mobile navigation',
              heading: navOption.label,
              category: CATEGORIES.MOBILE_NAV,
              activity: 'Link Click',
              url: window.location.href,
              language: i18n.language,
              title: document.title,
              sendToTealium: true,
              sendToCoreAPI: true,
              isMessageCenter: false
            }

            events.publish(options)
          }

          const subNav = (
            <SubNav
              analyticsCategory={navOption.analyticsCategory}
              menu={navOption.menu}
              menuName={navOption.label}
              sections={navOption.subsections}
              onBack={handleBackClick}
            />
          )
          subNavDict['mobile' + navOption.label] = subNav

          const handleNavOptionClick = () => {
            onNavOptionClick(subNav)

            dispatch(setOpenModal(`mobile${navOption.label}`))

            const options: Event = {
              authenticated: isLoggedIn,
              text: navOption.gaText || navOption.label,
              category: navOption.analyticsCategory,
              targetLocation: navOption.href,
              activity: 'Link Click',
              url: window.location.href,
              language: i18n.language,
              title: document.title,
              sendToTealium: true,
              sendToCoreAPI: true,
              isMessageCenter: false
            }

            events.publish(options)
          }
          if (navOption.isMessageCenter && enableMessageCenter) {
            return (
              <li key={navOption.id + navOption.label}>
                <MobileNavOption
                  onClick={() => {
                    dispatch(setSelectedMessageId(''))
                    handleCloseButtonClick()
                  }}
                  labelHasPotentialPii={navOption.labelHasPotentialPii}
                  unreadMessageCount={unreadMessageCount}
                  label={navOption.label}
                  icon={navOption.icon}
                  noCaret={!navOption.subsections && !navOption.menu}
                />
              </li>
            )
          } else {
            return (
              <li key={navOption.id + navOption.label}>
                <MobileNavOption
                  href={navOption.href}
                  onClick={handleNavOptionClick}
                  labelHasPotentialPii={navOption.labelHasPotentialPii}
                  label={navOption.label}
                  icon={navOption.icon}
                  noCaret={!navOption.subsections && !navOption.menu}
                />
              </li>
            )
          }
        })
      }
    </ul>
  )
}

interface MobileNavOptionProps {
  onClick?: () => void;
  label: string;
  labelHasPotentialPii?: boolean;
  lang?: 'es' | 'en';
  noCaret: boolean;
  href?: string;
  icon?: string;
  unreadMessageCount?: number;
}

const MobileNavOption: React.FC<MobileNavOptionProps> = ({ href, onClick, label, noCaret, icon, unreadMessageCount, lang, labelHasPotentialPii }) => {
  const ref = useRef<HTMLAnchorElement | HTMLButtonElement>(null)
  const buttonClassName = classNames(
    'ch-c-button ch-c-link ds-u-color--primary ds-u-text-decoration--none ch-c-mobile-nav__option-btn',
    labelHasPotentialPii ? 'qm-block-pii' : undefined
  )
  return (
    <CHButton
      href={href}
      className={buttonClassName}
      ref={ref}
      lang={lang}
      onClick={() => {
        if (ref.current) {
          ref.current.focus()
        }
        if (onClick) onClick()
      }}
    >

      <span className='ds-u-display--flex ds-u-align-items--center ds-u-justify-content--center'>
        {icon && <img className='ch-u-margin-right--8px' src={icon} alt='' />}
        <span className='ch-u-height--20 ch-u-mr--3'>{label}</span>
        {(unreadMessageCount && unreadMessageCount > 0) ? <Badge className='ch-c-badge ch-c-badge__orange' variation='warn'>{unreadMessageCount}</Badge> : ''}
      </span>
      {!noCaret && <span><CaretIcon className='ch-u-margin-bottom--2px ch-c-caret--right' focusable='false' /></span>}
    </CHButton>
  )
}

interface PrimaryNavProps {
  navLists: MobileResourceNavItem[][];
  languageToggleLink: string;
  onNavOptionClick: (arg: any) => void;
  loginUrl: string;
  logoutUrl: string;
  disableLogin: boolean;
  handleCloseButtonClick: () => void;
  enableMessageCenter: boolean;
}

const PrimaryNav: React.FC<PrimaryNavProps> = ({ handleCloseButtonClick, disableLogin, navLists, languageToggleLink, onNavOptionClick, loginUrl, logoutUrl, enableMessageCenter }) => {
  const { t } = useTranslation('common')
  const isLoggedIn = useSelector(getUserLoggedInState)
  const currentOpenModal = useSelector(getOpenModal)

  return (
    <>
      {
        navLists.map((navList, index) => {
          const bottomBorder = index < (navLists.length - 1)
          return (
            <MobileNavSubList
              currentOpenModal={currentOpenModal}
              handleCloseButtonClick={handleCloseButtonClick}
              navList={navList}
              onNavOptionClick={onNavOptionClick}
              key={index}
              bottomBorder={bottomBorder}
              enableMessageCenter={enableMessageCenter}
            />
          )
        })
      }

      <div>
        <MobileNavOption
          href={languageToggleLink}
          label={t('changeLanguage')}
          lang={getChangeLanguageLinkLang(i18n.language)}
          noCaret
          onClick={() => {
            const options: Event = {
              authenticated: isLoggedIn,
              text: t('changeLanguage'),
              targetLocation: languageToggleLink,
              category: CATEGORIES.LANGUAGE_SELECTION,
              activity: 'Link Click',
              url: window.location.href,
              language: i18n.language,
              title: document.title,
              sendToTealium: true,
              sendToCoreAPI: true,
              isMessageCenter: false
            }

            events.publish(options)
          }}
        />
      </div>

      {!disableLogin && (
        <div className='ch-u-p--20px'>
          {isLoggedIn
            ? (
              <CHButton
                className='ch-c-button ch-u-border-box ch-u-width--full'
                href={logoutUrl}
                onClick={async (event: any) => {
                  event.preventDefault()

                  const options: Event = {
                    authenticated: true,
                    text: t('Log out'),
                    targetLocation: logoutUrl,
                    category: CATEGORIES.PERSONAL_NAV,
                    activity: 'Button Click',
                    url: window.location.href,
                    language: i18n.language,
                    title: document.title,
                    sendToTealium: true,
                    sendToCoreAPI: true,
                    isMessageCenter: false
                  }
                  events.publish(options)
                  createAndEmitLogoutEvent()
                  await callEventHook('onLogout')

                  window.location.href = logoutUrl
                }}
              >
                {t('Log out')}
              </CHButton>
              )
            : (
              <CHButton
                className='ch-c-button ch-u-border-box ch-c-button--primary ch-u-width--full ch-u-color--white'
                href={loginUrl}
                onClick={() => {
                  const options: Event = {
                    authenticated: false,
                    text: t('Log in'),
                    targetLocation: loginUrl,
                    category: CATEGORIES.PERSONAL_NAV,
                    activity: 'Button Click',
                    url: window.location.href,
                    language: i18n.language,
                    title: document.title,
                    sendToTealium: true,
                    sendToCoreAPI: true,
                    isMessageCenter: false
                  }
                  events.publish(options)
                }}
              >
                {t('Log in')}
              </CHButton>
              )}
        </div>
      )}
    </>
  )
}

export interface MobileNavigationProps {
  className?: string;
  loginUrl: string;
  logoutUrl: string;
  navLists: (MobileResourceNavItem)[][];
  languageToggleLink: string;
  disableLogin: boolean;
  enableMessageCenter: boolean;
}

export const MobileNavigation: React.FC<MobileNavigationProps> = ({ loginUrl, logoutUrl, disableLogin, navLists, className, languageToggleLink, enableMessageCenter }) => {
  const [subNav, setSubNav] = useState<any | undefined>(undefined)
  const openBtnRef: React.RefObject<HTMLButtonElement> = useRef<HTMLButtonElement>(null)
  const isLoggedIn = useSelector(getUserLoggedInState)
  const currentOpenModal = useSelector(getOpenModal)
  const dispatch = useDispatch()

  useEffect(() => {
    if (currentOpenModal === '') {
      setSubNav(undefined)
    }
  }, [currentOpenModal])

  const handleMenuButtonClick = () => {
    if (currentOpenModal !== '') {
      handleCloseButtonClick()
    } else {
      dispatch(setOpenModal('mobileMenu'))

      const options: Event = {
        text: 'Menu',
        authenticated: isLoggedIn,
        category: CATEGORIES.MOBILE_NAV,
        targetLocation: 'Open mobile navigation',
        activity: 'Button Click',
        url: window.location.href,
        language: i18n.language,
        title: document.title,
        sendToTealium: true,
        sendToCoreAPI: true,
        isMessageCenter: false
      }
      events.publish(options)
    }
  }

  const handleCloseButtonClick = () => {
    dispatch(setOpenModal(''))
    setSubNav(undefined)

    if (openBtnRef.current) openBtnRef.current.focus()

    const options: Event = {
      text: 'X',
      authenticated: isLoggedIn,
      category: CATEGORIES.MOBILE_NAV,
      targetLocation: 'Close mobile navigation',
      activity: 'Button Click',
      url: window.location.href,
      language: i18n.language,
      title: document.title,
      sendToTealium: true,
      sendToCoreAPI: true,
      isMessageCenter: false
    }

    events.publish(options)
  }
  const { t } = useTranslation('common')
  const isOpen = currentOpenModal.startsWith('mobile')
  return (
    <>
      <nav aria-label={t(`Mobile Menu`)}>
        <button
          // TODO: Redesign this button to match the current design system button styles
          className={classNames('ch-c-button ch-c-button--primary-outline', 'ch-c-button--mobile-menu', 'ch-u-remove--lg', className)}
          onClick={handleMenuButtonClick}
          ref={openBtnRef}
          data-testid='mobile-nav-trigger'
          aria-expanded={currentOpenModal !== ''}
          type='button'
        >
          <span className='nav-menu-button-content-container ds-u-display--flex ds-u-align-items--center ds-u-font-size--base'>
            <img src={hamburgerIcon} alt='' width='24px' height='20px' className='ch-c-button--space-between' data-testid='veggieburger' />
            Menu
          </span>
        </button>
        {isOpen
          ? (
              <Drawer className={className} title='Menu' onClose={handleCloseButtonClick}>
                {subNav || (
                  <PrimaryNav
                    enableMessageCenter={enableMessageCenter}
                    handleCloseButtonClick={handleCloseButtonClick}
                    onNavOptionClick={setSubNav}
                    disableLogin={disableLogin}
                    loginUrl={loginUrl}
                    logoutUrl={logoutUrl}
                    navLists={navLists}
                    languageToggleLink={languageToggleLink}
                  />
                )}
              </Drawer>
            )
          : null
        }
      </nav>
    </>
  )
}
